// The Daily Progress Report's weather line.
//
// WHY THIS EXISTS, AND WHY IT IS SHAPED LIKE THIS. TDI-Brooks' own DPR — the one
// appended to their Jubilee Field report, see docs/DELIVERABLES.md §2.0 — opens
// with a block headed "Weather summary at 0700 hrs this day", and it has exactly
// six columns:
//
//     Wind (dir/kts) | Sea (type) | Sea (dir/ft) | Air | Visibility | Sky
//        SW/8kts     |   swell    |   SW/1-3ft   |     |   clear    | cloudy
//
// That is ONE observation a day, taken by eye off the bridge wing at 0700, and
// it is what a party chief writes into a contractual document. BOEM NTL
// 2022-G01 §IV.B.3 then requires "sea state and weather conditions" in the
// survey report itself. So this line is not decoration: it is required report
// content that is currently produced by looking out of a window.
//
// This module renders the same six fields from the forecast, in the DPR's own
// order, vocabulary and units, so the output can be read straight across into
// the form. Note in their own example the Air column is BLANK — that is how much
// of a low bar the incumbent method is.
//
// UNITS ARE THE FORM'S, NOT THE OPERATOR'S. Wind in knots and sea in FEET
// regardless of the console's metric/imperial preference, because the DPR is a
// fixed-format document and a block that changed shape with a display toggle
// would stop being paste-able. Air is the one column the form does not pin, so
// it follows the preference.
//
// WHAT IT IS NOT. A forecast for 0700 is not an observation at 0700. It does not
// replace the bridge log — it gives the party chief a defensible number to
// compare against, and it lets the same line be written in advance for a day not
// yet sailed. Every field says which it is.

import { compass16 } from "../data/stormlist.js";
import type { PointForecast } from "../forecast/openmeteo.js";
import { convTemp, tempUnit } from "../ui/units.js";

/** Metres → feet. Fixed, not preference-driven: the DPR's sea column is feet. */
const FT_PER_M = 3.280839895;

/** The hour a DPR weather summary is taken, in the site's local time. */
export const DPR_HOUR = 7;

/** One rendered DPR weather line. Any field may be null — the form has blanks. */
export interface DprWeather {
  /** Local date the line describes, "YYYY-MM-DD". */
  date: string;
  /** Index into fc.times that was used. */
  index: number;
  /** "SW/8kts" */
  wind: string | null;
  /** "swell" | "wind sea" | "mixed" */
  seaType: string | null;
  /** "SW/3ft" — significant height, see seaNote(). */
  sea: string | null;
  /** "24 °C" / "75 °F" */
  air: string | null;
  /** "clear" | "good" | "moderate" | "poor" | "fog" */
  visibility: string | null;
  /** "clear" | "partly cloudy" | "cloudy" | "overcast" */
  sky: string | null;
}

/**
 * Sea state as the form words it.
 *
 * The DPR writes a single word — their example is "swell" — so this reports
 * which train dominates rather than printing two heights. "mixed" is not a
 * hedge: when the two are comparable the sea genuinely is confused, and that is
 * the case a party chief most wants flagged.
 */
function seaType(swellH: number | null, windSeaH: number | null): string | null {
  if (swellH === null && windSeaH === null) return null;
  const sw = swellH ?? 0;
  const ws = windSeaH ?? 0;
  if (sw <= 0 && ws <= 0) return "calm";
  // 1.5 rather than a bare comparison: two trains within half of each other read
  // as one confused sea to an observer, not as one dominating the other.
  if (sw >= ws * 1.5) return "swell";
  if (ws >= sw * 1.5) return "wind sea";
  return "mixed";
}

/** Visibility in the words a bridge uses, from metres. */
function visWord(m: number | null): string | null {
  if (m === null || !Number.isFinite(m)) return null;
  if (m >= 20000) return "clear";
  if (m >= 10000) return "good";
  if (m >= 4000) return "moderate";
  if (m >= 1000) return "poor";
  return "fog";
}

/** Sky from total cloud cover, in the form's own vocabulary. */
function skyWord(pct: number | null): string | null {
  if (pct === null || !Number.isFinite(pct)) return null;
  if (pct < 15) return "clear";
  if (pct < 50) return "partly cloudy";
  if (pct < 85) return "cloudy";
  return "overcast";
}

const at = (s: readonly (number | null)[] | null | undefined, i: number): number | null =>
  s == null ? null : s[i] ?? null;

/**
 * Indices of the 0700 local hour for each day the forecast covers.
 *
 * fc.times are local wall-clock labels ("YYYY-MM-DDTHH:MM"), which is exactly
 * what a bridge observation is timed against — so this is a string match on the
 * hour, with no timezone arithmetic to get wrong.
 */
export function dprIndices(fc: PointForecast, hour = DPR_HOUR): number[] {
  const want = `T${String(hour).padStart(2, "0")}:`;
  const out: number[] = [];
  for (let i = 0; i < fc.times.length; i++) {
    if (fc.times[i].includes(want)) out.push(i);
  }
  return out;
}

/** Render the DPR weather line for one index into the forecast. */
export function dprWeatherAt(fc: PointForecast, i: number): DprWeather {
  const label = fc.times[i] ?? "";
  const windModel = fc.wind?.models.find((m) => m.data[i] != null) ?? null;
  const windKn = windModel ? at(windModel.data, i) : null;
  const windDir = at(fc.wind?.directionDeg ?? null, i);
  const waveModel = fc.waves?.height.find((m) => m.data[i] != null) ?? null;
  const waveM = waveModel ? at(waveModel.data, i) : null;
  const waveDir = at(fc.waves?.directionDeg ?? null, i);
  const airC = at(fc.airTempC ?? null, i);

  return {
    date: label.slice(0, 10),
    index: i,
    wind:
      windKn === null
        ? null
        : `${windDir === null ? "" : compass16(windDir) + "/"}${Math.round(windKn)}kts`,
    seaType: seaType(at(fc.partition?.swellH ?? null, i), at(fc.partition?.windSeaH ?? null, i)),
    sea:
      waveM === null
        ? null
        : `${waveDir === null ? "" : compass16(waveDir) + "/"}${Math.round(waveM * FT_PER_M)}ft`,
    air: airC === null ? null : `${Math.round(convTemp(airC))} ${tempUnit()}`,
    visibility: visWord(at(fc.visibilityM ?? null, i)),
    sky: skyWord(at(fc.cloudPct ?? null, i)),
  };
}

/** Every 0700 line the held forecast can fill. */
export function dprWeather(fc: PointForecast, hour = DPR_HOUR): DprWeather[] {
  return dprIndices(fc, hour).map((i) => dprWeatherAt(fc, i));
}

/**
 * The caveat that has to travel with the sea column.
 *
 * An observer writes a RANGE of individual wave heights ("1-3ft"); this is
 * significant height, the mean of the highest third, which is a different
 * quantity and a single number. Printing "3ft" where the form expects "1-3ft" is
 * fine — it is more defined, not less — but only if it says so.
 */
export const DPR_SEA_NOTE =
  "Sea is significant height (Hs) in feet — the mean of the highest third — not " +
  "the range of individual waves. Wind in knots.";

export const DPR_SOURCE_NOTE =
  "Forecast for 0700 local, not an observation. It does not replace the bridge log.";
